// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FulfillmentOrderStatusChip } from '@/components/fulfillment-order-status-chip';
import { Link } from '@/components/link';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { ArrowForward, ExpandMore } from '@/constants/hardfin-standard-icons';
import {
  FulfillmentOrder,
  FulfillmentOrderItem,
} from '@/fulfillment/api/types';
import { shipmentHasExtraItems } from '@/fulfillment/components/extra-items';
import { ExtraItemsAlert } from '@/fulfillment/components/extra-items-alert';
import { Shipment } from '@/shipment/api/types';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { useShipments } from '@/shipment/hooks/use-shipments';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MouseEvent, Suspense, useMemo, useState } from 'react';

interface Props {
  fulfillment: FulfillmentOrder;
}

interface DeliveredColumnProps {
  pending: number;
  shipped: number;
  delivered: number;
}

function DeliveredColumn(props: DeliveredColumnProps) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        padding: 1,
        minWidth: 180,
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="center">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Pending
          </Typography>
          <Typography variant="body2">{formatNumber(props.pending)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Shipped
          </Typography>
          <Typography variant="body2">{formatNumber(props.shipped)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Delivered
          </Typography>
          <Typography variant="body2">
            {formatNumber(props.delivered)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

interface RelatedShipmentsTableProps {
  shipments: Shipment[];
  productId?: string;
  partId?: string;
  foItemIds: Set<string>;
}

function RelatedShipmentsTable(props: RelatedShipmentsTableProps) {
  const { shipments, productId, partId } = props;

  const filteredShipmentItems = useMemo(() => {
    const items: {
      shipment: Shipment;
      quantity: number;
    }[] = [];

    // Match items by ID (productId and partId are now both just item IDs)
    const targetItemId = productId || partId;
    if (targetItemId) {
      shipments.forEach((shipment) => {
        shipment.items.forEach((item) => {
          if (item.id === targetItemId) {
            items.push({
              shipment,
              quantity: item.quantity ?? 0,
            });
          }
        });
      });
    }

    items.sort((a, b) => {
      const aDate = a.shipment.shippedAt?.toMillis() ?? 0;
      const bDate = b.shipment.shippedAt?.toMillis() ?? 0;

      if (bDate !== aDate) {
        return bDate - aDate;
      }

      return a.shipment.id.localeCompare(b.shipment.id);
    });

    return items;
  }, [shipments, productId, partId]);

  if (filteredShipmentItems.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No related shipments
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Related shipments
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Shipment ID</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Ship date</TableCell>
              <TableCell>Delivery date</TableCell>
              <TableCell>Tracking</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredShipmentItems.map((item, index) => {
              const { shipment, quantity } = item;

              return (
                <TableRow key={`${shipment.id}-${index}`}>
                  <TableCell>
                    <ShipmentStatusChip status={shipment.status} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Link
                        to="/shipments/$shipmentKey"
                        params={{ shipmentKey: shipment.key }}
                        search={{ type: 'outbound' }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        onClick={(e: MouseEvent) => e.stopPropagation()}
                      >
                        {shipment.shipmentNumber}
                        <ArrowForward fontSize="small" />
                      </Link>
                      {shipmentHasExtraItems(shipment, props.foItemIds) && (
                        <ExtraItemsAlert />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatNumber(quantity)}</TableCell>
                  <TableCell>
                    {shipment.shippedAt
                      ? shipment.shippedAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {shipment.deliveredAt
                      ? shipment.deliveredAt.toLocaleString({
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ShippingCarrierTrackingLabel
                      carrier={shipment.trackingNumberDetails?.carrier ?? null}
                      trackingIdentifier={
                        shipment.trackingNumberDetails?.trackingNumber ?? null
                      }
                      linkNumberDirectly={true}
                      variant="body2"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface LineItemRowProps {
  item: FulfillmentOrderItem;
  shipments: Shipment[];
  isLast: boolean;
  foItemIds: Set<string>;
}

function LineItemRow(props: LineItemRowProps) {
  const { item, shipments, isLast, foItemIds } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = (event: MouseEvent) => {
    event.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  const requested = item.requestedQuantity;
  const pending = item.pendingQuantity;
  const shipped = item.shippedQuantity;
  const delivered = item.deliveredQuantity;
  const need = Math.max(0, requested - (pending + shipped + delivered));

  const productName = item.item.name;
  const productSku = item.item.sku;

  return (
    <Box
      sx={{
        ...(!isLast && {
          borderBottom: '1px solid',
          borderColor: 'divider',
        }),
        paddingY: 1,
        paddingX: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={2} alignItems="center" flex={1}>
          <FulfillmentOrderStatusChip status={item.status} />
          <Box>
            <Typography variant="body2">
              {productSku}{' '}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                ({productName})
              </Typography>
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ textAlign: 'center', minWidth: 70 }}>
            <Typography variant="caption" color="text.secondary">
              Requested
            </Typography>
            <Typography variant="body2">{formatNumber(requested)}</Typography>
          </Box>

          <DeliveredColumn
            pending={pending}
            shipped={shipped}
            delivered={delivered}
          />

          <Box sx={{ textAlign: 'center', minWidth: 50 }}>
            <Typography variant="caption" color="text.secondary">
              Need
            </Typography>
            <Typography variant="body2">{formatNumber(need)}</Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleExpandClick}
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMore />
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={isExpanded}>
        <Box paddingY={1} paddingX={2}>
          <RelatedShipmentsTable
            shipments={shipments}
            productId={item.item.id}
            foItemIds={foItemIds}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function FulfillmentRowShipmentsContent(props: Props) {
  const { fulfillment } = props;
  const { shipments } = useShipments({
    filter: { foNumbers: [fulfillment.fulfillmentOrderNumber] },
  });

  // Build a set of item IDs from FO line items for detecting extra shipment items
  const foItemIds = useMemo(
    () => new Set(fulfillment.items.map((item) => item.item.id)),
    [fulfillment.items]
  );

  if (fulfillment.items.length === 0) {
    return (
      <Box sx={{ bgcolor: 'action.hover', p: 2, pl: 8 }}>
        <Typography variant="body2" color="text.secondary">
          No line items
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          Related line items
        </Typography>
        <Divider />
        {fulfillment.items.map((item, index) => (
          <LineItemRow
            key={item.id}
            item={item}
            shipments={shipments}
            isLast={index === fulfillment.items.length - 1}
            foItemIds={foItemIds}
          />
        ))}
      </Box>
    </Box>
  );
}

export function FulfillmentRowShipments(props: Props) {
  return (
    <Suspense
      fallback={
        <Box sx={{ bgcolor: 'action.hover', p: 2, pl: 8 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading shipments...
            </Typography>
          </Stack>
        </Box>
      }
    >
      <FulfillmentRowShipmentsContent fulfillment={props.fulfillment} />
    </Suspense>
  );
}
